import {
  AbsoluteCenter,
  Box,
  DrawerCloseButton,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import {
  AnimatedLogo,
  CustomDrawerBackButton,
  CustomDrawerFooter,
  CustomDrawerHeader,
  CustomFormErrorMessage,
  PrimaryButton,
  StatusBox,
} from '../../../../components/custom';
import { CustomDropdownMenu } from '../../../../components/custom/CustomDropdown';
import { InputGroupWithLoader } from '../../../../components/custom/CustomInputs';
import TransactionReceipt from '../../../../components/custom/TransactionReceipt';
import { DEFAULT_CREDIT_PROVIDERS } from '../../../../constants/billPaymentConstants';
import { currencyUnformat } from '../../../../helpers/utilityFunctions';
import useGetUIErrorMessage from '../../../../hooks/useGetUIErrorMessage';
import useHandleAmountChange from '../../../../hooks/useHandleAmountChange';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  clientIdValidationAsync,
  resetClientIdValidation,
} from '../../../../store/slices/bet9jaDeposits/clientIdValidationSlice';
import {
  processPaymentAsync,
  resetProcessPayment,
} from '../../../../store/slices/bet9jaDeposits/processPaymentSlice';
import CreateTransactionPin from '../../../TransactionPin/CreateTransactionPin';
import { TransactionPinForm } from '../TransactionPinForm';
import BetCreditsPurchaseSummary from './BetCreditsPurchaseSummary';

interface CreditsValues {
  providerName: string;
  clientId: string;
  amount: string;
}

interface BuyCreditsFormProps {
  clientIdFromProps?: string;
  providerNameFromProps?: string;
}

export default function BuyCreditsForm({
  clientIdFromProps,
  providerNameFromProps,
}: BuyCreditsFormProps) {
  const dispatch = useAppDispatch();
  const { status: idStatus, value: idValue } = useAppSelector(
    (state) => state.clientIdValidation
  );

  const [betCreditsValues, setBetCreditsValues] = useState<CreditsValues>({
    providerName: '',
    clientId: '',
    amount: '',
  });

  const initialValues = {
    providerName: providerNameFromProps ?? '',
    clientId: clientIdFromProps ?? '',
    amount: '',
  };

  const { status: paymentStatus, value: paymentValue } = useAppSelector(
    (state) => state.processPayment
  );

  const errorMessage = useGetUIErrorMessage({
    message: paymentValue?.message,
    errors: paymentValue?.errors,
    customMessage:
      'An error occurred while purchasing bet credits, please try again.',
  });

  const [amountError, setAmountError] = useState('');

  const businessNameFromStorage = localStorage.getItem('businessName');

  const validationSchema = Yup.object().shape({
    providerName: Yup.string().required('Provider Name is required'),
    clientId: Yup.string().required('User Id is required'),
    amount: Yup.string().required('Amount is required'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      setBetCreditsValues(values);
      handleStepChange();
    },
  });

  const {
    errors,
    touched,
    getFieldProps,
    setFieldValue,
    values,
    isValid,
    handleSubmit,
  } = formik;

  const handleAmountChange = useHandleAmountChange();

  const [step, setStep] = useState(0);

  const handleStepChange = () => {
    setStep(step + 1);
  };

  const handleStepBack = () => {
    setStep((step) => step - 1);
  };

  const handleClientIdValidation = () => {
    dispatch(clientIdValidationAsync({ clientId: values.clientId }));
  };

  const clientIdErrorMessage = useGetUIErrorMessage({
    message: idValue?.message,
    errors: idValue?.errors,
  });

  const handlePurchaseCredit = ({ pin }: { pin: string }) => {
    dispatch(
      processPaymentAsync({
        pin: pin.toString(),
        amount: currencyUnformat(values.amount) * 100,
        clientId: values.clientId,
        customerAccountName: businessNameFromStorage ?? '',
        reference: idValue?.data?.reference ?? '',
        username: idValue?.data?.username ?? '',
      })
    );
  };

  const accountNumberFromStorage = localStorage.getItem('accountNumber');
  const accountNameFromStorage = localStorage.getItem('accountName');

  const [userHasPin, setUserHasPin] = useState(
    localStorage.getItem('userHasPin')
  );

  if (!userHasPin) {
    return <CreateTransactionPin setUserHasPin={setUserHasPin} />;
  }

  if (paymentStatus === 'loading') {
    return (
      <Box
        position="relative"
        zIndex="2"
        height="80vh"
        top="-120px"
        background="#fff"
      >
        <AbsoluteCenter>
          <AnimatedLogo />
        </AbsoluteCenter>
      </Box>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <>
        <CustomDrawerHeader startsFromBeginning>
          Betting Receipt
        </CustomDrawerHeader>
        <TransactionReceipt
          date={new Date().toDateString()}
          amount={currencyUnformat(values.amount)}
          transactionType="Bill Payment"
          transactionReference={'----'}
          senderName={accountNameFromStorage ?? ''}
          narration="test"
          provider={values.providerName}
          device={values.clientId}
          transactionFee="₦0.00"
          senderAccountNumber={accountNumberFromStorage ?? ''}
          showAmountInWords={false}
        />
        <CustomDrawerFooter>
          <PrimaryButton
            as={DrawerCloseButton}
            w="full"
            top="initial"
            right="initial"
            onClick={() => {
              dispatch(resetClientIdValidation());
              dispatch(resetProcessPayment());
            }}
          >
            Done
          </PrimaryButton>
        </CustomDrawerFooter>
      </>
    );
  }

  if (paymentStatus === 'failed') {
    return (
      <>
        <Box
          position="relative"
          zIndex="2"
          height="80vh"
          top="-120px"
          background="#fff"
        >
          <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
            <StatusBox variant="error" title="Failed to process payment">
              {errorMessage}
            </StatusBox>
          </AbsoluteCenter>
        </Box>
        <CustomDrawerFooter>
          <PrimaryButton
            onClick={() => dispatch(resetProcessPayment())}
            w="full"
          >
            Retry
          </PrimaryButton>
        </CustomDrawerFooter>
      </>
    );
  }

  const steps = [
    <FormikProvider value={formik} key={1}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Box display="flex" flexDirection="column" py="18px">
          <Stack spacing={8} flex="1">
            <Box mb={6}>
              <Text fontWeight="500" color="#000" mb={1}>
                Recipient Information
              </Text>
              <Text color="gray.300" fontSize=".875rem">
                Streamline transactions with seamless information management
              </Text>
            </Box>

            <FormControl
              id="providerName"
              isInvalid={Boolean(touched.providerName && errors.providerName)}
            >
              <FormLabel>Provider Name</FormLabel>
              <CustomDropdownMenu
                value={values.providerName}
                options={DEFAULT_CREDIT_PROVIDERS.map((provider) => ({
                  label: provider,
                  value: provider,
                }))}
                fieldValue="providerName"
                placeholder="Select Provider Name"
                setFieldValue={setFieldValue}
                menuWidth="450px"
              />
              <CustomFormErrorMessage>
                {touched.providerName && errors.providerName}
              </CustomFormErrorMessage>
            </FormControl>

            <FormControl
              id="clientId"
              isInvalid={
                Boolean(touched.clientId && errors.clientId) ||
                idStatus === 'failed'
              }
            >
              <FormLabel>Client ID</FormLabel>
              <InputGroupWithLoader isLoading={idStatus === 'loading'}>
                <Input
                  {...getFieldProps('clientId')}
                  isDisabled={idStatus === 'loading'}
                  onBlur={handleClientIdValidation}
                  placeholder="Enter your ID"
                  maxLength={20}
                />
              </InputGroupWithLoader>
              <CustomFormErrorMessage>
                {(touched.clientId && errors.clientId) || clientIdErrorMessage}
              </CustomFormErrorMessage>
            </FormControl>

            <FormControl
              id="customerName"
              display={idStatus === 'success' ? 'initial' : 'none'}
            >
              <FormLabel>Customer Name</FormLabel>
              <Input
                isDisabled
                value={idValue?.data?.firstName + ' ' + idValue?.data?.lastName}
              />
            </FormControl>

            <FormControl
              id="amount"
              isInvalid={Boolean(
                touched.amount && (errors.amount || amountError)
              )}
            >
              <FormLabel>Amount</FormLabel>
              <InputGroup>
                <InputLeftElement
                  pointerEvents="none"
                  children="₦"
                  height="100%"
                />
                <Input
                  {...getFieldProps('amount')}
                  placeholder="Enter amount"
                  onChange={(e) => {
                    handleAmountChange({
                      e,
                      setFieldValue,
                      fieldValue: 'amount',
                      setErrorMessage: setAmountError,
                      checkForAvailableBalance: true,
                    });
                  }}
                />
              </InputGroup>
              <CustomFormErrorMessage>
                {touched.amount && (errors.amount || amountError)}
              </CustomFormErrorMessage>
            </FormControl>
          </Stack>

          <CustomDrawerFooter>
            <PrimaryButton
              isDisabled={!isValid || !!amountError || idStatus !== 'success'}
              type="submit"
              w="full"
            >
              Continue
            </PrimaryButton>
          </CustomDrawerFooter>
        </Box>
      </Form>
    </FormikProvider>,
    <Box key={2}>
      <CustomDrawerBackButton onClick={handleStepBack} />
      <BetCreditsPurchaseSummary
        summary={{
          providerName: betCreditsValues.providerName,
          accountName: idValue?.data?.firstName + ' ' + idValue?.data?.lastName,
          clientId: betCreditsValues.clientId,
          amount: betCreditsValues.amount,
          date: new Date(),
        }}
        handleSubmit={handleStepChange}
      />
    </Box>,
    <Box key={3}>
      <CustomDrawerBackButton onClick={handleStepBack} />
      <TransactionPinForm
        onSubmit={handlePurchaseCredit}
        step={step}
        setStep={setStep}
        statusBoxTitle={paymentValue?.message}
        statusBoxText=""
        twoFactor={{
          type: 'betting',
          amountKobo: Math.round(currencyUnformat(values.amount) * 100),
        }}
      />
    </Box>,
  ];

  return steps[step];
}
